import {
  SimpleGrid,
  Stack,
  Title,
  LoadingOverlay,
  Text,
  Skeleton,
  Paper,
  Center,
} from '@mantine/core';
import { MapEvent } from 'mapbox-gl';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './result-maps.module.css';

import { FORM_STATUS, ProductRel, ZoningFeatureCollection } from '@data-access';
import {
  LegendControl,
  PfMap,
  ZoningLayer,
  ZoningLegend,
  ZoningLegendProps,
  zoomOnFeature,
} from '@map';

const Pending = () => (
  <Skeleton animate={false} h="100%" pos="relative" radius="xs">
    <LoadingOverlay visible />
  </Skeleton>
);

const MissingRealYieldMap = () => (
  <Paper bg="gray.0" h="100%" pos="relative" radius="xs" shadow="unset">
    <Center h="100%">
      <Text maw={400} size="lg">
        Aucune carte de rendement réel pour cette parcelle.
      </Text>
    </Center>
  </Paper>
);

const Error = () => (
  <Stack className={classes.fullContainer}>
    <Title order={3} size="h2">
      Nous avons rencontré une erreur
    </Title>
    <Text size="lg">
      Une erreur est survenue lors de la génération du conseil. Merci de
      réessayer plus tard.
    </Text>
  </Stack>
);

interface ResultMapsProps {
  status: FORM_STATUS;
  estimatedYieldMap: ZoningFeatureCollection | null;
  realYieldMap: ZoningFeatureCollection | null;
  product?: ProductRel | null;
  estimatedYieldLegend: {
    total: number;
    mean: number;
  } | null;
  realYieldLegend: {
    total: number;
    mean: number;
  } | null;
}

export function ResultMaps({
  status,
  estimatedYieldMap,
  realYieldMap,
  product,
  estimatedYieldLegend,
  realYieldLegend,
}: ResultMapsProps) {
  const { t } = useTranslation();

  const estimatedYieldmapLegend: ZoningLegendProps = useMemo(() => {
    if (estimatedYieldMap) {
      const l = estimatedYieldMap.features.reduce((acc, feature) => {
        return {
          ...acc,
          [feature.properties.zone]: {
            zone: feature.properties.zone,
            color: feature.properties.color,
            value: feature.properties.value.toFixed(2),
            percent: feature.properties.percent,
            area: feature.properties.area,
          },
        };
      }, {});

      return {
        title: t('common.yield-map-legend-title'),
        legend: Object.values(l),
        unit: 'q/ha',
        mean: estimatedYieldLegend?.mean,
      };
    }
    return {
      legend: [],
    };
  }, [t, estimatedYieldMap, estimatedYieldLegend]);

  const realYieldmapLegend: ZoningLegendProps = useMemo(() => {
    if (realYieldMap) {
      const l = realYieldMap.features.reduce((acc, feature) => {
        return {
          ...acc,
          [feature.properties.zone]: {
            zone: feature.properties.zone,
            color: feature.properties.color,
            value: feature.properties.value.toFixed(2),
            percent: feature.properties.percent,
            area: feature.properties.area,
          },
        };
      }, {});

      return {
        title: t('common.yield-map-legend-title'),
        legend: Object.values(l),
        unit: 'q/ha',
        mean: realYieldLegend?.mean,
      };
    }
    return {
      legend: [],
    };
  }, [t, realYieldMap, realYieldLegend]);

  const handleMapLoad = (e: MapEvent, map: ZoningFeatureCollection) => {
    if (map) {
      zoomOnFeature({
        map: e.target,
        geojson: map,
      });
    }
  };

  if (status === FORM_STATUS.ERROR) {
    return <Error />;
  }

  return (
    <SimpleGrid className={classes.grid} cols={2}>
      <Stack className={classes.col}>
        <Title order={3}>{t('common.estimated_yield_map')}</Title>
        {status !== FORM_STATUS.PENDING && estimatedYieldMap ? (
          <PfMap
            classnames={{
              container: classes.mapContainer,
            }}
            onLoad={(e) => handleMapLoad(e, estimatedYieldMap)}
          >
            <ZoningLayer data={estimatedYieldMap} filled outline={false} />
            <LegendControl>
              <ZoningLegend {...estimatedYieldmapLegend} />
            </LegendControl>
          </PfMap>
        ) : (
          <Pending />
        )}
      </Stack>
      <Stack className={classes.col}>
        <Title order={3}>{t('common.real_yield_map')}</Title>
        {status !== FORM_STATUS.PENDING && realYieldMap ? (
          <PfMap
            classnames={{
              container: classes.mapContainer,
            }}
            onLoad={(e) => handleMapLoad(e, realYieldMap)}
          >
            <ZoningLayer data={realYieldMap} filled outline={false} />
            <LegendControl>
              <ZoningLegend
                {...realYieldmapLegend}
                product={product}
                switchable
              />
            </LegendControl>
          </PfMap>
        ) : (
          <MissingRealYieldMap />
        )}
      </Stack>
    </SimpleGrid>
  );
}

export default ResultMaps;
